import React, { useEffect, useState, useRef } from "react";
import {
  ArrowRight, PlugZap, ChevronLeft, ChevronRight,
  Download, Shield, Lock, Monitor
} from "lucide-react";
import Logo from '../common/Logo';
import SEO from '../common/SEO';

export default function Home() {
  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <SEO
        title="GA4.so — AI Agent for Google Analytics 4 & Search Console"
        description="Native macOS app that connects to your GA4 and Search Console. Ask questions in plain English, get root cause analysis, ML forecasts, and client-ready reports."
        path="/"
        rawTitle
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "GA4.so — AI Agent for Google Analytics 4",
          "description": "Native macOS app for GA4 and GSC analytics automation. Ask in plain English, get deep analysis and client-ready reports.",
          "url": "https://www.ga4.so/",
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "GA4.so",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "macOS",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "$10 free credit, then ~$0.09 per agent session"
            }
          }
        }}
      />

      {/* ── NAV ── */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-brand-gray-dark">
            <a href="#features" className="hover:text-primary transition-colors">Features</a>
            <a href="#how" className="hover:text-primary transition-colors">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <NavDownloadButton />
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-10 pb-0 bg-background">
        <div className="container mx-auto px-6 flex flex-col items-center text-center max-w-2xl">
          <h1 className="text-5xl md:text-6xl font-bold text-[#404880] leading-[1.5] tracking-tight">
            AI Agent for{" "}
            <span className="text-brand-pink">Google Analytics 4</span>
          </h1>
          <HeroNudge />
        </div>
      </section>

      {/* ── FEATURES CAROUSEL ── */}
      <section id="features" className="pt-6 pb-10 bg-background">
        <div className="container mx-auto px-6">
          <FeaturesCarousel />
        </div>
      </section>


      {/* ── PRIVACY / BYOK ── */}
      <section className="container mx-auto px-6 pb-20">
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Privacy by design</h2>
          <p className="text-brand-gray-medium text-sm">Your data stays on your machine. Always.</p>
        </div>
        <div className="bg-[#404880] rounded-2xl p-8 md:p-12 overflow-hidden relative border border-white/8">
          <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-brand-pink/8 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-primary/8 rounded-full blur-[100px] pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/8 border border-white/15 px-3 py-1.5 text-xs text-white/60 mb-5">
                <Shield size={12} className="text-brand-pink" />
                Built for privacy
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                Your data never leaves<br />your machine
              </h2>
              <p className="text-white/55 leading-relaxed text-base">
                GA4.so is a native macOS app. Your analytics data is fetched and processed locally. No cloud storage. No third-party data access. You bring your own Anthropic API key — we never see it.
              </p>
            </div>
            <div className="space-y-3">
              <PrivacyCard icon={<Lock size={16} />} title="Data stays on your device">
                Analytics data is fetched directly on your Mac. Nothing is stored on our servers — not your queries, not your results, not your data.
              </PrivacyCard>
              <PrivacyCard icon={<PlugZap size={16} />} title="Bring your own API key">
                Connect your own Anthropic key. You pay Claude directly — no markup, full cost visibility.
              </PrivacyCard>
              <PrivacyCard icon={<Shield size={16} />} title="Read-only GA4 access">
                OAuth scope is read-only. The agent analyses your data but can never modify it.
              </PrivacyCard>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="container mx-auto px-6 pb-20">
        <div className="rounded-2xl border border-border bg-background/70 p-8 md:p-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Up and running in minutes</h2>
            <p className="text-brand-gray-medium">Connect once. Ask anything. No setup hell.</p>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-0">
            <HowStep number="1" title="Download">
              Install the macOS app. One DMG, no browser extension needed.
            </HowStep>
            <div className="hidden md:flex items-center justify-center pt-5 flex-shrink-0 w-8">
              <ArrowRight size={16} className="text-brand-gray-medium" />
            </div>
            <HowStep number="2" title="Connect GA4 + GSC">
              Sign in with Google. Read-only OAuth access to your properties.
            </HowStep>
            <div className="hidden md:flex items-center justify-center pt-5 flex-shrink-0 w-8">
              <ArrowRight size={16} className="text-brand-gray-medium" />
            </div>
            <HowStep number="3" title="Add your API key">
              Paste your Anthropic key. You pay Claude directly — no markup.
            </HowStep>
            <div className="hidden md:flex items-center justify-center pt-5 flex-shrink-0 w-8">
              <ArrowRight size={16} className="text-brand-gray-medium" />
            </div>
            <HowStep number="4" title="Ask anything">
              Type a question. The agent plans, fetches data, runs analysis, and delivers.
            </HowStep>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border/50">
        <div className="container mx-auto px-6 py-10 text-sm text-brand-gray-medium flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo size="small" />
          <div className="flex flex-wrap gap-6 justify-center">
            <a href="#how"      className="hover:text-primary transition-colors">How it works</a>
            <a href="https://blogs.ga4.so/" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Blog</a>
            <a href="/privacy-policy"   className="hover:text-primary transition-colors">Privacy</a>
            <a href="/terms-of-service" className="hover:text-primary transition-colors">Terms</a>
          </div>
          <p>© {new Date().getFullYear()} GA4.so</p>
        </div>
      </footer>
    </div>
  );
}

/* ── Nav download button ── */
function NavDownloadButton() {
  const arch = useDownloadArch();
  const url  = `https://api.ga4.so/api/desktop-update/download/${arch}`;
  return (
    <a href={url} className="text-sm px-4 py-2 rounded-lg bg-brand-pink text-white hover:opacity-90 flex items-center gap-1.5 font-medium transition-all hover:shadow-lg hover:shadow-brand-pink/25">
      <Download size={14} /> Download for Mac
    </a>
  );
}

/* ── Features Carousel ── */
const SLIDES = [
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433275/ga4-so/_1_askQuestion.png',
    title: 'Just ask in plain English',
    desc: 'Request any dashboard, report, or audit. The Analyst builds the workspace for you automatically.',
  },
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433275/ga4-so/_2_answer_analysis.png',
    title: 'Forward-looking projections',
    desc: 'Get growth targets, budget models, and channel-mix forecasts as client-ready visuals.',
  },
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433276/ga4-so/_3_answer_analysis.png',
    title: 'Complete deliverables, ready to use',
    desc: 'One request creates dashboards, documents, CSV exports, and strategic insights — all saved locally.',
  },
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433275/ga4-so/_4_dashboard_interactive.png',
    title: 'Interactive dashboards',
    desc: 'Explore your data live — hover, compare, and spot underperforming channels instantly.',
  },
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433275/ga4-so/_5_google_analytics4_property_connect.png',
    title: 'Connect GA4 in one click',
    desc: 'Link your Google Analytics 4 property to pull real data into every report.',
  },
  {
    img: 'https://res.cloudinary.com/zipscores-collection/image/upload/v1781433275/ga4-so/_6_google_search_console_connect.png',
    title: 'Add Search Console',
    desc: 'Blend in SEO metrics — clicks, impressions, CTR, and position — for the full picture.',
  },
];

function FeaturesCarousel() {
  const [active, setActive] = useState(1);

  const go = (idx) => {
    setActive((idx + SLIDES.length) % SLIDES.length);
  };

  const restart = (idx) => {
    go(idx);
  };

  const slide = SLIDES[active];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Description — above dot nav */}
      <p className="text-center text-[#404880]/60 text-sm leading-relaxed max-w-md mx-auto mb-3 min-h-[40px]">
        {slide.desc}
      </p>

      {/* Dot nav */}
      <div className="flex items-center justify-center gap-2 mb-5">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => restart(i)}
            className={`rounded-full transition-all duration-300 ${i === active ? 'w-6 h-2 bg-brand-pink' : 'w-2 h-2 bg-border hover:bg-brand-gray-medium'}`}
          />
        ))}
      </div>

      {/* Screenshot */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border bg-muted/30">
        {SLIDES.map((s, i) => (
          <img
            key={i}
            src={s.img}
            alt={s.title}
            className={`w-full h-auto block transition-opacity duration-700 ${i === active ? 'opacity-100' : 'opacity-0'}`}
            style={i !== active ? { position: 'absolute', top: 0, left: 0, pointerEvents: 'none' } : {}}
          />
        ))}

        {/* Prev / Next */}
        <button
          onClick={() => restart(active - 1)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#404880]/70 border border-[#404880]/40 flex items-center justify-center hover:bg-[#404880] transition-colors shadow-md"
        >
          <ChevronLeft size={18} className="text-white" />
        </button>
        <button
          onClick={() => restart(active + 1)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#404880]/70 border border-[#404880]/40 flex items-center justify-center hover:bg-[#404880] transition-colors shadow-md"
        >
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>

      {/* Title — below screenshot */}
      <h3 className="text-center text-base md:text-lg font-semibold text-foreground mt-5">
        {slide.title}
      </h3>
    </div>
  );
}

/* ──────────────────────────────────────────
   Download arch detection + CTA
────────────────────────────────────────── */
function HeroNudge() {
  const arch   = useDownloadArch();
  const isARM  = arch === 'arm64';
  const url    = `https://api.ga4.so/api/desktop-update/download/${arch}`;
  const altUrl = `https://api.ga4.so/api/desktop-update/download/${isARM ? 'x64' : 'arm64'}`;
  const chip    = isARM ? 'Apple Silicon' : 'Intel';
  const altChip = isARM ? 'Intel Mac' : 'Apple Silicon Mac';

  return (
    <div className="mt-6 flex flex-col items-center gap-2.5">
      <a
        href={url}
        className="inline-flex items-center gap-2 bg-brand-pink text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity shadow-md shadow-brand-pink/20"
      >
        <Download size={14} />
        Download for Mac
        <span className="bg-white/20 px-2 py-0.5 rounded-full text-[11px] font-normal">{chip}</span>
      </a>
      <p className="text-[11px] text-[#404880]/40 flex items-center gap-2">
        <span className="text-[#404880]/65 font-semibold">100% free</span>
        <span className="text-[#404880]/20">·</span>
        <span>macOS 13+</span>
        <span className="text-[#404880]/20">·</span>
        <a href={altUrl} className="hover:text-[#404880]/70 underline underline-offset-2 transition-colors">
          {altChip}?
        </a>
        <span className="text-[#404880]/20">·</span>
        <span className="text-[#404880]/30 italic">Windows coming soon</span>
      </p>
    </div>
  );
}

function useDownloadArch() {
  const [arch, setArch] = useState('arm64'); // default: Apple Silicon (most modern Macs)
  useEffect(() => {
    const ua = navigator.userAgentData;
    if (!ua?.getHighEntropyValues) return; // Safari — keep arm64 default
    ua.getHighEntropyValues(['architecture'])
      .then(({ architecture }) => {
        if (architecture === 'x86' || architecture === 'x86_64') setArch('x64');
      })
      .catch(() => {});
  }, []);
  return arch;
}

function DownloadCTA() {
  const arch = useDownloadArch();
  const isARM  = arch === 'arm64';
  const url    = `https://api.ga4.so/api/desktop-update/download/${arch}`;
  const altUrl = `https://api.ga4.so/api/desktop-update/download/${isARM ? 'x64' : 'arm64'}`;
  const chip    = isARM ? 'Apple Silicon' : 'Intel';
  const altChip = isARM ? 'Intel' : 'Apple Silicon';

  return (
    <div className="flex flex-col gap-3">
      <a
        href={url}
        className="inline-flex items-center gap-2.5 bg-brand-pink text-white px-7 py-4 rounded-xl font-semibold hover:opacity-90 transition-all hover:shadow-xl hover:shadow-brand-pink/30 text-[15px] self-start"
      >
        <Download size={17} />
        Download for Mac
        <span className="bg-white/20 text-[11px] px-2 py-0.5 rounded-full font-normal">{chip}</span>
      </a>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        <span className="text-[#404880]/50">macOS 13+ · Free while in beta</span>
        <a
          href={altUrl}
          className="text-[#404880]/60 hover:text-[#404880] underline underline-offset-2 transition-colors"
        >
          {altChip} Mac?
        </a>
        <span className="text-[#404880]/40">Windows coming soon</span>
      </div>
    </div>
  );
}

/* ── Privacy card ── */
function PrivacyCard({ icon, title, children }) {
  return (
    <div className="flex items-start gap-3 bg-white/4 border border-white/8 rounded-xl p-4">
      <div className="p-2 rounded-lg bg-white/8 text-white flex-shrink-0">{icon}</div>
      <div>
        <h4 className="text-sm font-semibold text-white mb-1">{title}</h4>
        <p className="text-[11px] text-white/45 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/* ── How it works step ── */
function HowStep({ number, title, children }) {
  return (
    <div className="flex-1 flex flex-col items-center text-center px-2">
      <div className="w-10 h-10 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
        {number}
      </div>
      <h4 className="font-semibold text-foreground mb-2">{title}</h4>
      <p className="text-sm text-brand-gray-dark leading-relaxed">{children}</p>
    </div>
  );
}

